export * from './applicationPaths.js';
export type WorkAreaLocale = 'de' | 'en' | 'tr';
export const workAreaCategories = ['pflege', 'handwerk', 'einzelhandel'] as const;
export type WorkAreaCategory = (typeof workAreaCategories)[number];

/** Stable form/page keys and stored profession codes are deliberately separate. */
export const careWorkAreas = [
  { key: 'pflegefachkraft', profession: 'hemsire', icon: 'icon-[material-symbols--medical-services-outline-rounded]' },
  { key: 'op-pflege-ota', profession: 'ameliyathane-hemsiresi', icon: 'icon-[material-symbols--surgical]' },
  { key: 'radiologie-mtra', profession: 'mtra', icon: 'icon-[material-symbols--radiology]' },
  {
    key: 'medizinische-fachangestellte',
    profession: 'mfa',
    icon: 'icon-[material-symbols--clinical-notes-outline-rounded]',
  },
  { key: 'physiotherapie', profession: 'fizyoterapist', icon: 'icon-[material-symbols--physical-therapy]' },
  { key: 'labor-gesundheit', profession: 'laborant', icon: 'icon-[material-symbols--biotech-rounded]' },
  {
    key: 'weitere-gesundheitsberufe',
    profession: 'diger',
    icon: 'icon-[material-symbols--health-and-safety-outline-rounded]',
  },
] as const;
export type CareWorkAreaKey = (typeof careWorkAreas)[number]['key'];
export const careWorkAreaKeys = careWorkAreas.map(area => area.key);
export const getCareWorkArea = (key: string | undefined) => careWorkAreas.find(area => area.key === key);
export const isCareWorkAreaKey = (key: string): key is CareWorkAreaKey => !!getCareWorkArea(key);

const careCopy: Record<WorkAreaLocale, Record<CareWorkAreaKey, readonly [string, string, readonly string[]]>> = {
  de: {
    pflegefachkraft: [
      'Pflegefachkraft',
      'Pflege, Versorgung und persönliche Begleitung von Menschen.',
      ['Pflege', 'Versorgung', 'Teamarbeit'],
    ],
    'op-pflege-ota': [
      'OP-Pflegefachkraft / OTA',
      'Patienten im OP begleiten, Eingriffe vorbereiten und im Operationsteam arbeiten.',
      ['OP', 'Vorbereitung', 'Teamarbeit'],
    ],
    'radiologie-mtra': [
      'Radiologietechnologe / MTRA',
      'Erfahrung in Bildgebung, Untersuchungsabläufen und medizinischer Technik.',
      ['Radiologie', 'Bildgebung', 'Technik'],
    ],
    'medizinische-fachangestellte': [
      'Medizinische Fachangestellte / MFA',
      'Patienten betreuen, Praxisabläufe organisieren und das medizinische Team unterstützen.',
      ['Praxis', 'Patientenkontakt', 'Organisation'],
    ],
    physiotherapie: [
      'Physiotherapeut/in',
      'Menschen bei Bewegung, Therapie und Rehabilitation begleiten.',
      ['Therapie', 'Bewegung', 'Rehabilitation'],
    ],
    'labor-gesundheit': [
      'Labor / technisches Gesundheitspersonal',
      'Sorgfalt, Analytik und technische Erfahrung im Gesundheitswesen.',
      ['Labor', 'Analytik', 'Gesundheit'],
    ],
    'weitere-gesundheitsberufe': [
      'Sonstiger Gesundheitsberuf',
      'Deinen weiteren Gesundheitsberuf und deine Erfahrung individuell vorstellen.',
      ['Gesundheit', 'Erfahrung', 'Persönlich'],
    ],
  },
  en: {
    pflegefachkraft: ['Nurse', 'Nursing, patient care and personal support.', ['Nursing', 'Patient care', 'Teamwork']],
    'op-pflege-ota': [
      'Surgical nurse / OR technician (OTA)',
      'Support patients in theatre, prepare procedures and work in the surgical team.',
      ['Operating theatre', 'Preparation', 'Teamwork'],
    ],
    'radiologie-mtra': [
      'Radiology technologist / MTRA',
      'Experience in imaging, examination workflows and medical technology.',
      ['Radiology', 'Imaging', 'Technology'],
    ],
    'medizinische-fachangestellte': [
      'Medical assistant / MFA',
      'Support patients, organise practice workflows and assist the medical team.',
      ['Medical practice', 'Patients', 'Organisation'],
    ],
    physiotherapie: [
      'Physiotherapist',
      'Support people with movement, therapy and rehabilitation.',
      ['Therapy', 'Movement', 'Rehabilitation'],
    ],
    'labor-gesundheit': [
      'Laboratory / technical health staff',
      'Careful analytical work and technical experience in healthcare.',
      ['Laboratory', 'Analysis', 'Healthcare'],
    ],
    'weitere-gesundheitsberufe': [
      'Other healthcare profession',
      'Tell us about your other healthcare profession and individual experience.',
      ['Healthcare', 'Experience', 'Personal support'],
    ],
  },
  tr: {
    pflegefachkraft: [
      'Hemşire',
      'Hemşirelik, hasta bakımı ve kişisel destek.',
      ['Hemşirelik', 'Hasta bakımı', 'Ekip çalışması'],
    ],
    'op-pflege-ota': [
      'Ameliyathane hemşiresi / OTA',
      'Ameliyathanede hastalara destek ol, işlemleri hazırla ve cerrahi ekibinde çalış.',
      ['Ameliyathane', 'Hazırlık', 'Ekip çalışması'],
    ],
    'radiologie-mtra': [
      'Radyoloji teknikeri / MTRA',
      'Görüntüleme, tetkik süreçleri ve tıbbi teknoloji deneyimi.',
      ['Radyoloji', 'Görüntüleme', 'Teknoloji'],
    ],
    'medizinische-fachangestellte': [
      'Tıbbi asistan / MFA',
      'Hastalara destek ol, muayenehane süreçlerini düzenle ve tıbbi ekibe yardımcı ol.',
      ['Muayenehane', 'Hasta iletişimi', 'Organizasyon'],
    ],
    physiotherapie: [
      'Fizyoterapist',
      'Hareket, tedavi ve rehabilitasyon süreçlerinde insanlara destek ol.',
      ['Tedavi', 'Hareket', 'Rehabilitasyon'],
    ],
    'labor-gesundheit': [
      'Laboratuvar / teknik sağlık personeli',
      'Sağlık alanında dikkatli analiz ve teknik çalışma deneyimi.',
      ['Laboratuvar', 'Analiz', 'Sağlık'],
    ],
    'weitere-gesundheitsberufe': [
      'Diğer sağlık mesleği',
      'Diğer sağlık mesleğini ve kişisel deneyimini bizimle paylaş.',
      ['Sağlık', 'Deneyim', 'Kişisel destek'],
    ],
  },
};
export const careWorkAreaCopy = (key: CareWorkAreaKey, locale: WorkAreaLocale) => {
  const [label, description, tags] = careCopy[locale][key];
  return { label, description, tags };
};
export const workAreaCategoryLabel = (category: WorkAreaCategory, locale: WorkAreaLocale): string =>
  ({
    de: { pflege: 'Pflege & Gesundheit', handwerk: 'Handwerk & Technik', einzelhandel: 'Einzelhandel & Gastronomie' },
    en: { pflege: 'Care & healthcare', handwerk: 'Skilled trades & technology', einzelhandel: 'Retail & gastronomy' },
    tr: { pflege: 'Bakım ve sağlık', handwerk: 'Zanaat ve teknik', einzelhandel: 'Perakende ve gastronomi' },
  })[locale][category];

const craftKeys = new Set(['handwerk', 'elektriker', 'bauwesen', 'schweisser', 'cnc', 'shk', 'lkw-fahrer']);
const retailKeys = new Set(['einzelhandel', 'gastronomie', 'gastro', 'baecker', 'metzger', 'frischetheke']);
export const workAreaCategoryForKey = (key: string): WorkAreaCategory | undefined =>
  key === 'pflege' || isCareWorkAreaKey(key)
    ? 'pflege'
    : craftKeys.has(key)
      ? 'handwerk'
      : retailKeys.has(key)
        ? 'einzelhandel'
        : undefined;

/** Indexes remain source indexes so reordered menus keep the existing editor mappings. */
export const groupIndustryNavigation = <T extends { href: string }>(items: readonly T[]) => {
  const indexed = items.map((item, index) => ({
    item,
    index,
    key: item.href.match(/(?:\/branchen\/|#)([^/?#]+)/u)?.[1] ?? '',
  }));
  return {
    groups: workAreaCategories.map(category => ({
      category,
      parent: indexed.find(entry => entry.key === category),
      children: indexed.filter(entry => entry.key !== category && workAreaCategoryForKey(entry.key) === category),
    })),
    ungrouped: indexed.filter(entry => !workAreaCategoryForKey(entry.key)),
  };
};

export const retailWorkAreaTags = (
  key: 'gastronomie' | 'baecker' | 'metzger' | 'frischetheke',
  locale: WorkAreaLocale,
): readonly string[] =>
  ({
    de: {
      gastronomie: ['Küche', 'Service', 'Gastfreundschaft'],
      baecker: ['Produktion', 'Backwaren', 'Verkauf'],
      metzger: ['Produktion', 'Verarbeitung', 'Verkauf'],
      frischetheke: ['Käse', 'Fisch', 'Fleisch'],
    },
    en: {
      gastronomie: ['Kitchen', 'Service', 'Hospitality'],
      baecker: ['Production', 'Bakery goods', 'Sales'],
      metzger: ['Production', 'Processing', 'Sales'],
      frischetheke: ['Cheese', 'Fish', 'Meat'],
    },
    tr: {
      gastronomie: ['Mutfak', 'Servis', 'Misafirperverlik'],
      baecker: ['Üretim', 'Unlu mamuller', 'Satış'],
      metzger: ['Üretim', 'İşleme', 'Satış'],
      frischetheke: ['Peynir', 'Balık', 'Et'],
    },
  })[locale][key];
